import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { useAuth } from "@/context/AuthContext";
import { HeaderStickyBanner } from "@/components/AdPlacements";
import {
  ShoppingCart,
  MagnifyingGlass,
  Funnel,
  Star,
  StarHalf,
  ArrowSquareOut,
  Tag,
  Truck,
  ShieldCheck,
  CaretDown,
  Heart,
  Tractor,
  TShirt,
  Boot,
  Wrench,
  Cpu,
  Book,
  PawPrint,
  Tree,
  Gift,
  CircleNotch,
  X
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL || '';

// Category icons mapping
const CATEGORY_ICONS = {
  workwear: TShirt,
  footwear: Boot,
  tools: Wrench,
  tech: Cpu,
  books: Book,
  animal: PawPrint,
  outdoor: Tree,
  gifts: Gift
};

// Star rating component
function StarRating({ rating, reviewCount }) {
  if (!rating) return null;
  
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  
  return (
    <div className="flex items-center gap-1">
      <div className="flex">
        {[...Array(fullStars)].map((_, i) => (
          <Star key={i} size={14} weight="fill" className="text-[#F59E0B]" />
        ))}
        {hasHalfStar && <StarHalf size={14} weight="fill" className="text-[#F59E0B]" />}
        {[...Array(5 - fullStars - (hasHalfStar ? 1 : 0))].map((_, i) => (
          <Star key={`empty-${i}`} size={14} className="text-[#D1D5DB]" />
        ))}
      </div>
      {reviewCount && (
        <span className="text-xs text-[#6B6661]">({reviewCount.toLocaleString()})</span>
      )}
    </div>
  );
}

// Product Card Component
function ProductCard({ product, onClickThrough }) {
  const discount = product.original_price 
    ? Math.round((1 - product.price / product.original_price) * 100) 
    : null;

  const handleClick = () => {
    // Track the click
    onClickThrough(product.id);
    // Open Amazon link in new tab
    window.open(product.amazon_url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div 
      className="bg-white border border-[#E0D4C8] group hover:shadow-lg hover:border-[#0F766E] transition-all cursor-pointer"
      onClick={handleClick}
      data-testid={`product-${product.id}`}
    >
      {/* Image */}
      <div className="relative aspect-square bg-[#F9FAFB] overflow-hidden">
        <img 
          src={product.image_url} 
          alt={product.title}
          className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform"
          loading="lazy"
        />
        {discount && (
          <span className="absolute top-2 left-2 px-2 py-1 bg-[#DC2626] text-white text-xs font-mono">
            -{discount}%
          </span>
        )}
        {product.featured && (
          <span className="absolute top-2 right-2 px-2 py-1 bg-[#0F766E] text-white text-xs font-mono">
            Featured
          </span>
        )}
      </div>

      {/* Details */}
      <div className="p-4">
        {/* Brand */}
        {product.brand && (
          <p className="text-xs text-[#6B6661] font-medium uppercase tracking-wider mb-1">
            {product.brand}
          </p>
        )}

        {/* Title */}
        <h3 className="font-sans text-sm text-[#33302E] line-clamp-2 mb-2 min-h-[40px] group-hover:text-[#0F766E]">
          {product.title}
        </h3>

        {/* Rating */}
        <StarRating rating={product.rating} reviewCount={product.review_count} />

        {/* Price */}
        <div className="mt-3 flex items-baseline gap-2">
          <span className="font-mono text-lg text-[#33302E]">
            €{product.price.toFixed(2)}
          </span>
          {product.original_price && (
            <span className="font-mono text-sm text-[#8B8580] line-through">
              €{product.original_price.toFixed(2)}
            </span>
          )}
        </div>

        {/* Amazon CTA */}
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-[#6B6661] flex items-center gap-1">
            <Truck size={14} />
            Amazon Prime
          </span>
          <span className="text-xs text-[#0F766E] flex items-center gap-1 group-hover:underline">
            View on Amazon
            <ArrowSquareOut size={14} />
          </span>
        </div>
      </div>
    </div>
  );
}

export default function FarmShopPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "");
  const [selectedGender, setSelectedGender] = useState(searchParams.get("gender") || "");
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [sortBy, setSortBy] = useState(searchParams.get("sort") || "featured");
  const [showFilters, setShowFilters] = useState(false);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/api/shop/categories`);
        setCategories(response.data.categories);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = { sort_by: sortBy, limit: 100 };
        if (selectedCategory) params.category = selectedCategory;
        if (selectedGender) params.gender = selectedGender;
        if (searchQuery) params.search = searchQuery;

        const response = await axios.get(`${API}/api/shop/products`, { params });
        setProducts(response.data.products);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();

    // Update URL params
    const params = new URLSearchParams();
    if (selectedCategory) params.set("category", selectedCategory);
    if (selectedGender) params.set("gender", selectedGender);
    if (searchQuery) params.set("q", searchQuery);
    if (sortBy !== "featured") params.set("sort", sortBy);
    setSearchParams(params);
  }, [selectedCategory, selectedGender, searchQuery, sortBy, setSearchParams]);

  // Track click-through
  const handleClickThrough = async (productId) => {
    try {
      await axios.post(`${API}/api/shop/products/${productId}/click`);
    } catch (error) {
      // Silent fail for tracking
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSelectedCategory("");
    setSelectedGender("");
    setSearchQuery("");
    setSortBy("featured");
  };

  const hasActiveFilters = selectedCategory || selectedGender || searchQuery;

  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      <SEOHead 
        title="Farm Shop - Quality Products for Farmers"
        description="Shop quality workwear, boots, tools, and equipment for farmers. Curated selection of farming essentials delivered by Amazon."
        url="/shop"
      />
      <HeaderStickyBanner />
      <Header />

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2 py-1 bg-[#0F766E] text-white text-xs font-mono uppercase tracking-wider">
              Farm Shop
            </span>
            <span className="px-2 py-1 bg-[#FEF3C7] text-[#92400E] text-xs font-mono uppercase tracking-wider">
              Amazon Affiliate
            </span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#33302E] mb-2">
            Quality Products for Farmers
          </h1>
          <p className="text-[#6B6661] font-sans max-w-2xl">
            Curated selection of workwear, boots, tools, and equipment. All products delivered by Amazon with Prime delivery available.
          </p>
        </div>

        {/* Trust Badges */}
        <div className="flex flex-wrap gap-4 mb-8 text-sm text-[#6B6661]">
          <div className="flex items-center gap-2">
            <ShieldCheck size={20} className="text-[#0F766E]" />
            <span>Trusted by Amazon</span>
          </div>
          <div className="flex items-center gap-2">
            <Truck size={20} className="text-[#0F766E]" />
            <span>Prime Delivery</span>
          </div>
          <div className="flex items-center gap-2">
            <Tag size={20} className="text-[#0F766E]" />
            <span>Best Prices</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar - Categories */}
          <div className="lg:col-span-1">
            {/* Mobile Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="lg:hidden w-full flex items-center justify-between px-4 py-3 bg-white border border-[#E0D4C8] mb-4"
            >
              <span className="flex items-center gap-2">
                <Funnel size={18} />
                Filters
              </span>
              <CaretDown size={18} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>

            <div className={`space-y-6 ${showFilters ? 'block' : 'hidden lg:block'}`}>
              {/* Categories */}
              <div className="bg-white border border-[#E0D4C8] p-4">
                <h3 className="font-sans font-medium text-[#33302E] mb-3">Categories</h3>
                <div className="space-y-1">
                  <button
                    onClick={() => setSelectedCategory("")}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors ${
                      !selectedCategory ? "bg-[#F0FDF4] text-[#0F766E]" : "text-[#6B6661] hover:bg-[#F9FAFB]"
                    }`}
                  >
                    <ShoppingCart size={16} />
                    All Products
                  </button>
                  {categories.map((cat) => {
                    const Icon = CATEGORY_ICONS[cat.id] || ShoppingCart;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => setSelectedCategory(cat.id)}
                        className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors ${
                          selectedCategory === cat.id ? "bg-[#F0FDF4] text-[#0F766E]" : "text-[#6B6661] hover:bg-[#F9FAFB]"
                        }`}
                      >
                        <Icon size={16} />
                        {cat.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Gender Filter */}
              <div className="bg-white border border-[#E0D4C8] p-4">
                <h3 className="font-sans font-medium text-[#33302E] mb-3">Shop For</h3>
                <div className="space-y-1">
                  {[
                    { id: "", name: "Everyone" },
                    { id: "male", name: "Men" },
                    { id: "female", name: "Women" },
                  ].map((gender) => (
                    <button
                      key={gender.id}
                      onClick={() => setSelectedGender(gender.id)}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                        selectedGender === gender.id ? "bg-[#F0FDF4] text-[#0F766E]" : "text-[#6B6661] hover:bg-[#F9FAFB]"
                      }`}
                    >
                      {gender.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Affiliate Disclosure */}
              <div className="bg-[#FEF3C7] border border-[#FCD34D] p-4 text-xs text-[#92400E]">
                <p className="font-medium mb-1">Affiliate Disclosure</p>
                <p>
                  As an Amazon Associate, Farmwatch earns from qualifying purchases. 
                  This helps support our platform at no extra cost to you.
                </p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Search & Sort Bar */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              {/* Search */}
              <div className="relative flex-1">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6661]" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-[#E0D4C8] bg-white text-sm focus:outline-none focus:border-[#0F766E]"
                />
              </div>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2.5 border border-[#E0D4C8] bg-white text-sm focus:outline-none focus:border-[#0F766E]"
              >
                <option value="featured">Featured</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="newest">Newest</option>
                <option value="popular">Most Popular</option>
              </select>
            </div>

            {/* Active Filters */}
            {hasActiveFilters && (
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="text-sm text-[#6B6661]">Active filters:</span>
                {selectedCategory && (
                  <span className="px-2 py-1 bg-[#F0FDF4] text-[#0F766E] text-xs flex items-center gap-1">
                    {categories.find(c => c.id === selectedCategory)?.name || selectedCategory}
                    <button onClick={() => setSelectedCategory("")}><X size={12} /></button>
                  </span>
                )}
                {selectedGender && (
                  <span className="px-2 py-1 bg-[#F0FDF4] text-[#0F766E] text-xs flex items-center gap-1">
                    {selectedGender === "male" ? "Men" : "Women"}
                    <button onClick={() => setSelectedGender("")}><X size={12} /></button>
                  </span>
                )}
                {searchQuery && (
                  <span className="px-2 py-1 bg-[#F0FDF4] text-[#0F766E] text-xs flex items-center gap-1">
                    "{searchQuery}"
                    <button onClick={() => setSearchQuery("")}><X size={12} /></button>
                  </span>
                )}
                <button
                  onClick={clearFilters}
                  className="text-xs text-[#DC2626] hover:underline"
                >
                  Clear all
                </button>
              </div>
            )}

            {/* Results Count */}
            <p className="text-sm text-[#6B6661] mb-4">
              Showing {products.length} products
              {selectedCategory && ` in ${categories.find(c => c.id === selectedCategory)?.name}`}
            </p>

            {/* Products Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <CircleNotch size={40} className="animate-spin text-[#0F766E]" />
              </div>
            ) : products.length === 0 ? (
              <div className="bg-white border border-[#E0D4C8] p-12 text-center">
                <ShoppingCart size={48} className="text-[#D1D5DB] mx-auto mb-4" />
                <h3 className="font-serif text-xl text-[#33302E] mb-2">No products found</h3>
                <p className="text-[#6B6661] mb-4">
                  {searchQuery 
                    ? `No results for "${searchQuery}". Try a different search term.`
                    : "Products coming soon! Check back later."}
                </p>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 bg-[#0F766E] text-white hover:bg-[#0D6B64]"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                {products.map((product) => (
                  <ProductCard 
                    key={product.id} 
                    product={product} 
                    onClickThrough={handleClickThrough}
                  />
                ))}
              </div>
            )}

            {/* Bottom CTA */}
            {products.length > 0 && (
              <div className="mt-12 bg-[#0F766E] text-white p-8 text-center">
                <Tractor size={48} className="mx-auto mb-4" />
                <h3 className="font-serif text-2xl mb-2">Can't find what you're looking for?</h3>
                <p className="text-[#A7F3D0] mb-4">
                  Browse millions more products on Amazon with Prime delivery.
                </p>
                <a
                  href="https://www.amazon.co.uk/s?k=farming+equipment&tag=farmshop04-21"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#0F766E] font-medium hover:bg-[#F0FDF4] transition-colors"
                >
                  Browse Amazon
                  <ArrowSquareOut size={18} />
                </a>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
